/**
 * Utility to get the API URL from environment variables
 * Ensures /api is always appended and handles production vs development
 */

/**
 * Get the API base URL (with /api suffix)
 * In development, returns relative URL to use Vite proxy
 * In production, uses VITE_API_URL if set, otherwise falls back to /api (Vercel proxy)
 */
export const getApiUrl = (): string => {
  // Check if we're in development mode
  const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';
  
  // In development mode, use relative URL to leverage Vite proxy
  if (isDev) {
    return '/api';
  }
  
  // In production, check for VITE_API_URL
  const baseUrl = import.meta.env.VITE_API_URL;
  
  if (baseUrl) {
    // Remove trailing slash if present
    const cleanUrl = baseUrl.replace(/\/$/, '');
    // Ensure /api is appended
    return cleanUrl.endsWith('/api') ? cleanUrl : `${cleanUrl}/api`;
  }
  
  // Fallback: Use relative /api which will be proxied by Vercel (if vercel.json is configured)
  // This allows the app to work even if VITE_API_URL is not set
  console.warn('⚠️ VITE_API_URL is not set in production. Using /api proxy (requires vercel.json configuration).');
  return '/api';
};

/**
 * Get the backend base URL (without /api)
 * Used for serving static files like uploads
 */
export const getBackendBaseUrl = (): string => {
  const apiUrl = getApiUrl();
  if (!apiUrl) return '';
  // Remove /api to get base URL
  return apiUrl.replace(/\/api\/?$/, '');
};

